import * as R from 'ramda';

import config from '@atom/selectors/config';
import { Media, MediaFolder, MediaFolderTree } from '@atom/types/media';
import naturalSort from '@atom/utilities/naturalSortCollator';
import { getUserFullName } from '@atom/utilities/userUtilities';

import { ROOT_FOLDER_ID, ROOT_FOLDER_LABEL } from './constants';
import { Breadcrumb, MediaSortColumn } from './types';

export const canCreateSubfolder = (folderDepth: number): boolean =>
  folderDepth < config.MAX_FOLDER_DEPTH;

export const validateFolderName = (name: string): string => {
  if (name.length === 0) {
    return 'The folder name cannot be blank';
  }

  if (name.length > config.MAX_CHARACTERS_FOLDER_NAME) {
    return `The folder name cannot exceed ${config.MAX_CHARACTERS_FOLDER_NAME} characters`;
  }

  return '';
};

export const findFolderPath = (
  tree: MediaFolderTree | undefined,
  folderId: string,
): MediaFolderTree[] | null => {
  if (!tree) {
    return null;
  }

  if (tree.id === folderId) {
    return [tree];
  }

  const children = tree.children ?? [];

  return children.reduce<MediaFolderTree[] | null>((found, child) => {
    if (found) {
      return found;
    }

    const sub = findFolderPath(child, folderId);
    return sub ? [tree, ...sub] : null;
  }, null);
};

export const buildBreadcrumbs = (
  tree: MediaFolderTree | undefined,
  selectedFolderId: string,
): Breadcrumb[] => {
  if (!tree) {
    return [{ id: ROOT_FOLDER_ID, name: ROOT_FOLDER_LABEL }];
  }

  const root = { id: tree.id, name: ROOT_FOLDER_LABEL };
  if (selectedFolderId === tree.id || selectedFolderId === ROOT_FOLDER_ID) {
    return [root];
  }

  const path = findFolderPath(tree, selectedFolderId);
  if (!path) {
    return [root];
  }

  return [
    root,
    ...path.slice(1).map(folder => ({ id: folder.id, name: folder.name })),
  ];
};

export const treeWithPendingChild = (
  tree: MediaFolderTree,
  parentId: string,
  pending: MediaFolderTree,
): MediaFolderTree => {
  if (tree.id === parentId) {
    return { ...tree, children: [pending, ...(tree.children ?? [])] };
  }

  if (!tree.children) {
    return tree;
  }

  return {
    ...tree,
    children: tree.children.map(child =>
      treeWithPendingChild(child, parentId, pending),
    ),
  };
};

export const sortMedia = <T extends Media | MediaFolder>(
  sortColumn: MediaSortColumn | null,
  isAscending: boolean,
  data: T[],
): T[] => {
  if (!sortColumn) {
    return data;
  }

  const accessor = (item: T): string => {
    if (sortColumn === 'createdBy') {
      return item.createdBy ? getUserFullName(item.createdBy) : '-';
    }

    if (sortColumn === 'fileExtension') {
      return 'fileExtension' in item ? item.fileExtension : '';
    }

    const value = item[sortColumn];
    return R.isNil(value) ? '' : String(value);
  };

  const direction = isAscending ? 1 : -1;
  return R.sort(
    (first, second) =>
      direction * naturalSort(accessor(first), accessor(second)),
    data,
  );
};
